/*
 * HARUM (HAR-208): wishlist stored in the customer's own browser.
 *
 * Daniel picked option (a) in Linear: "I build it into the theme, saved in the
 * customer's browser - free, but doesn't follow them across devices". So there
 * is deliberately no backend and no wishlist app here. If cross-device sync is
 * ever wanted, this file is the seam to replace.
 *
 * Storage: localStorage key `harum:wishlist:v1`, an array of plain objects:
 *   { id, variant_id, handle, title, url, image, price, vendor, added_at }
 *
 * `price` is the money-formatted string captured when the item was saved, so a
 * later currency switch can leave it stale. That is a deliberate trade-off for
 * not needing a request per card; the card links to the product for the live
 * price.
 *
 * The version suffix in the key exists so a future shape change can start
 * clean instead of trying to migrate half-written data.
 */
(function () {
  const KEY = 'harum:wishlist:v1';
  const EVENT = 'harum:wishlist:change';

  function read() {
    try {
      const raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.id) : [];
    } catch (error) {
      /* Private-mode Safari and quota errors both land here; an empty
         wishlist is a better outcome than a broken page. */
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch (error) {
      /* Nothing useful to do - the in-memory list still reflects the click,
         and the next page load simply shows the last persisted state. */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { items } }));
    return items;
  }

  function sameId(a, b) {
    return String(a) === String(b);
  }

  const Wishlist = {
    key: KEY,
    event: EVENT,

    items() {
      return read();
    },

    count() {
      return read().length;
    },

    has(id) {
      return read().some((item) => sameId(item.id, id));
    },

    add(item) {
      if (!item || !item.id) return read();
      const items = read();
      if (items.some((existing) => sameId(existing.id, item.id))) return items;
      items.unshift(Object.assign({ added_at: Date.now() }, item));
      return write(items);
    },

    remove(id) {
      return write(read().filter((item) => !sameId(item.id, id)));
    },

    toggle(item) {
      if (this.has(item.id)) {
        this.remove(item.id);
        return false;
      }
      this.add(item);
      return true;
    },

    clear() {
      return write([]);
    },

    subscribe(handler) {
      document.addEventListener(EVENT, handler);
      /* Another tab writing the same key fires `storage`, not our own event. */
      window.addEventListener('storage', (event) => {
        if (event.key === KEY) handler(new CustomEvent(EVENT, { detail: { items: read() } }));
      });
    },
  };

  window.HarumWishlist = Wishlist;

  /* ---- Toggle buttons (product page, and anywhere else the snippet lands) ---- */

  function syncButton(button) {
    const saved = Wishlist.has(button.dataset.wishlistId);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.classList.toggle('is-saved', saved);

    const labelDefault = button.dataset.wishlistLabelDefault || 'Add to wishlist';
    const labelSaved = button.dataset.wishlistLabelSaved || 'Saved to wishlist';

    button.setAttribute('aria-label', saved ? labelSaved : labelDefault);
    const label = button.querySelector('[data-wishlist-label]');
    if (label) {
      label.textContent = saved
        ? button.dataset.wishlistLabelSaved || 'Saved to wishlist'
        : button.dataset.wishlistLabelDefault || 'Add to wishlist';
    }
  }

  function syncAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(syncButton);
  }

  function itemFromButton(button) {
    const data = button.dataset;
    if (!data.wishlistId) return null;
    return {
      id: data.wishlistId,
      variant_id: data.wishlistVariantId || '',
      handle: data.wishlistHandle || '',
      title: data.wishlistTitle || '',
      url: data.wishlistUrl || '',
      image: data.wishlistImage || '',
      price: data.wishlistPrice || '',
      vendor: data.wishlistVendor || '',
    };
  }

  function onClick(event) {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();

    const item = itemFromButton(button);
    if (!item) return;

    Wishlist.toggle(item);
    syncButton(button);
  }

  document.addEventListener('click', onClick);
  document.addEventListener('DOMContentLoaded', syncAllButtons);
  document.addEventListener(EVENT, syncAllButtons);
  if (document.readyState !== 'loading') syncAllButtons();
})();
